import { type FC, useCallback } from 'react'
import { Button, Flex, VStack } from '@chakra-ui/react'
import { type AddToCartPayload, RelatedProductTypes } from 'ecosystem'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import ProductDetailPrice from '../../ProductDetailPrice'
import VariantPicker from '../../VariantPicker/VariantPicker'
import ProductBundles from '../../ProductBundles'
import ProductSubProducts from '../../ProductSubProducts'
import ProductDetailAddToCart from '../../ProductDetailAddToCart'
import { useProductContext } from '../../../hooks/useProductContext'
import type { RegularProductInformationOverrides } from './types'
import useRegularVariants from './useRegularVariants'

const RegularProductPurchaseData: FC = () => {
  const overrides = useOverridesContext<keyof RegularProductInformationOverrides>()
  const { product, onAddToCart, relatedGroups, bundles, onProductRequest, deliveryRenderer } =
    useProductContext()

  const hasPriceRequest = product.priceRequest
  const { selectVariant, selectVariantOnUpdate, isVariantSelected, prices } =
    useRegularVariants(product)

  const handleAddRegularProductToCart = useCallback(
    (payload: AddToCartPayload) => {
      const subItems = bundles
        ? bundles.map((bundleItem) => ({
            ...bundleItem,
            originalQty: bundleItem.qty,
            qty: String(Number(bundleItem.qty) * Number(payload.qty))
          }))
        : []
      onAddToCart({ ...payload.product, qty: payload.qty, subItems })
    },
    [onAddToCart, bundles]
  )

  return (
    <>
      {!hasPriceRequest ? <ProductDetailPrice {...prices} /> : null}

      <VStack pb={6} w="100%">
        <VariantPicker
          onSelectVariant={selectVariant}
          onVariantsUpdate={selectVariantOnUpdate}
          variantGroupId={product.variantGroup}
          {...{ isVariantSelected }}
        />
        <ProductBundles />
        <ProductSubProducts subProducts={relatedGroups[RelatedProductTypes.CUSTOM] || []} />
      </VStack>

      {hasPriceRequest ? (
        <Flex justify="center" w="full">
          <Button
            aria-label={overrideText('Offertförfrågan', overrides?.productRequest)}
            fontSize="md"
            onClick={() => {
              onProductRequest(product)
            }}
            size="lg"
            w="100%">
            {overrideText('Offertförfrågan', overrides?.productRequest)}
          </Button>
        </Flex>
      ) : (
        <ProductDetailAddToCart onAddToCart={handleAddRegularProductToCart} totalPrices={prices} />
      )}
      {deliveryRenderer()}
    </>
  )
}

export default RegularProductPurchaseData
